// Note image upload — multipart POST to BE which proxies to Garage and
// generates 3 WebP variants (thumb 256w / md 768w / lg 1600w) at upload time.
// HEIC must be converted client-side before calling.
//
// Response shape: each variant has its own URL + key. The legacy
// `public_url` field aliases variants.orig.url so existing callers keep
// working; new code should use `base_path` + `imageUrl()` to pick a variant.
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import { rememberFreshUpload } from "@/lib/fresh-upload";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";
const PUBLIC_BASE: string | undefined = import.meta.env.VITE_S3_PUBLIC_BASE_URL;

const ALLOWED = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

export type Variant = "orig" | "thumb" | "md" | "lg";

export interface VariantInfo {
  url: string;
  key: string;
  contentType: string;
  width?: number;
  height?: number;
}

export interface UploadResult {
  base_path: string;
  variants: Record<Variant, VariantInfo>;
  /** Alias for variants.orig.url — kept for legacy callers. */
  public_url: string;
  /** Alias for variants.orig.key — kept for legacy callers. */
  key: string;
}

export async function uploadImageToR2(file: File): Promise<UploadResult> {
  if (!ALLOWED.has(file.type)) {
    if (file.type === "image/heic" || file.type === "image/heif") {
      throw new Error(
        "HEIC không được hỗ trợ trực tiếp — vui lòng chuyển sang JPEG trước khi upload.",
      );
    }
    throw new Error("Chỉ nhận ảnh JPG, PNG, WebP hoặc GIF — chọn ảnh khác");
  }

  const form = new FormData();
  form.append("file", file, file.name);

  const resp = await postFormAuthed(`${API_BASE}/notes/upload`, form);
  if (!resp.ok) {
    // The BE refuses with the {error:{code,message}} envelope; a proxy in
    // front of it may answer with a page instead, which gives no reason.
    const body: unknown = await resp.json().catch(() => null);
    throw new Error(serverErrorMessage(body, "Không tải được ảnh lên — thử lại"));
  }
  const result = (await resp.json()) as UploadResult;
  rememberFreshUpload(result.base_path, result.variants?.orig?.url || result.public_url);
  return result;
}

/**
 * Build a public URL for the given base_path + variant.
 *
 * Accepts either:
 *   - a base_path returned by uploadImageToR2 ("note-images/2026/05/<uuid>"),
 *     in which case we append `/<variant>.<ext>`;
 *   - a legacy full URL (anything starting with http), which is returned
 *     as-is for back-compat with rows still holding pre-migration values.
 */
export function imageUrl(basePathOrUrl: string, variant: Variant = "md"): string {
  if (!basePathOrUrl) return "";
  if (/^https?:\/\//i.test(basePathOrUrl)) return basePathOrUrl;
  if (!PUBLIC_BASE) {
    // Dev without CDN: surface the raw key so callers can plug in a
    // signed URL later. Don't silently emit a broken absolute URL.
    return basePathOrUrl;
  }
  const ext = variant === "orig" ? "" : ".webp";
  // For orig we don't know the extension here — callers that need orig
  // should use variants.orig.url from the upload response, not this helper.
  // When ext is "", just return the base path so the absence is visible.
  if (variant === "orig") return `${PUBLIC_BASE.replace(/\/$/, "")}/${basePathOrUrl}/orig`;
  return `${PUBLIC_BASE.replace(/\/$/, "")}/${basePathOrUrl}/${variant}${ext}`;
}
